import { useMemo, useState } from 'react';
import { FileDown, FileText, Mail } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { calculateVPELines, type VpeLine } from '@growthdept/blocks/gift-pricing';
import { ClientPackagePdf } from '@/pdf/ClientPackagePdf';
import { SupplierOrderPdf } from '@/pdf/SupplierOrderPdf';
import { supplierProductIdentity } from '@/lib/product-search';
import type { Client, GiftPackage, GiftProduct, GiftSupplier, Project } from '@/lib/types';

type LineWithProduct = VpeLine<GiftProduct>;

interface Props {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportButtons({ project, pkg, client }: Props) {
  const [busy, setBusy] = useState<string | null>(null);

  const vpeLines = useMemo(
    () =>
      calculateVPELines<GiftProduct>((pkg.items ?? []) as never, pkg.quantity) as LineWithProduct[],
    [pkg.items, pkg.quantity],
  );

  const suppliersInPkg = useMemo<GiftSupplier[]>(() => {
    const seen = new Map<string, GiftSupplier>();
    for (const item of pkg.items ?? []) {
      const p = item.gift_product;
      if (!p?.supplier?.id) continue;
      if (!seen.has(p.supplier.id)) seen.set(p.supplier.id, p.supplier);
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [pkg.items]);

  async function downloadClientPdf() {
    setBusy('client');
    try {
      const blob = await pdf(<ClientPackagePdf project={project} pkg={pkg} client={client} />).toBlob();
      const filename = `pakket-${slug(project.name)}-${slug(client?.company_name ?? 'klant')}.pdf`;
      await downloadBlob(blob, filename);
    } finally {
      setBusy(null);
    }
  }

  async function downloadSupplierPdf(supplier: GiftSupplier) {
    setBusy(`s-${supplier.id}`);
    try {
      const blob = await pdf(
        <SupplierOrderPdf
          project={project}
          pkg={pkg}
          client={client}
          supplier={supplier}
          vpeLines={vpeLines}
        />,
      ).toBlob();
      const filename = `inkoop-${slug(supplier.name)}-${slug(project.name)}.pdf`;
      await downloadBlob(blob, filename);
    } finally {
      setBusy(null);
    }
  }

  function copyMailDraft(supplier: GiftSupplier) {
    const items = vpeLines.filter((l) => l.product.supplier_id === supplier.id);
    if (items.length === 0) return;
    const lines = items.map(
      (l) => {
        const identity = supplierProductIdentity(l.product);
        const identityText = identity.articleNumber
          ? `art.nr ${identity.articleNumber}${identity.ean ? ` · EAN ${identity.ean}` : ''}`
          : `ARTIKELCODE ONTBREEKT${identity.ean ? ` · EAN ${identity.ean}` : ''}`;
        return `  • ${identityText} — ${l.product.name} — ${l.total_ordered} stuks (${l.product.vpe} per colli)`;
      },
    );
    const subject = `Inkoopopdracht ${project.name} — ${client?.company_name ?? ''}`;
    const body = [
      `Beste ${supplier.name},`,
      '',
      `Graag ontvangen wij voor het project "${project.name}" (${pkg.quantity} pakketten) het volgende:`,
      '',
      ...lines,
      '',
      `Project-referentie: ${project.id.slice(0, 8)}`,
      pkg.handling_date ? `Gewenste leverdatum: ${pkg.handling_date}` : '',
      '',
      'Met vriendelijke groet,',
      '2BMade',
    ]
      .filter(Boolean)
      .join('\n');
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  return (
    <div className="space-y-3 rounded-xl border border-bmade-200 bg-white p-4 dark:border-bmade-800 dark:bg-bmade-900/40">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300">
          Export
        </h3>
        <span className="text-[10px] text-bmade-500">{suppliersInPkg.length} leveranciers</span>
      </div>

      <button
        type="button"
        onClick={downloadClientPdf}
        disabled={busy === 'client' || (pkg.items?.length ?? 0) === 0}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-40"
      >
        <FileText className="h-4 w-4" />
        {busy === 'client' ? 'Bezig...' : 'PDF voor klant'}
      </button>

      {suppliersInPkg.length > 0 && (
        <div className="space-y-1.5 border-t border-bmade-200 pt-3 dark:border-bmade-800">
          <div className="text-[10px] uppercase tracking-wider text-bmade-500">
            Per leverancier
          </div>
          {suppliersInPkg.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <span className="truncate text-bmade-800 dark:text-bmade-200">{s.name}</span>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => downloadSupplierPdf(s)}
                  disabled={busy === `s-${s.id}`}
                  className="inline-flex items-center gap-1 rounded border border-bmade-200 px-2 py-1 text-bmade-700 hover:border-accent-400 hover:text-accent-700 disabled:opacity-40 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:text-accent-300"
                  title="Download PDF"
                >
                  <FileDown className="h-3 w-3" />
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => copyMailDraft(s)}
                  className="inline-flex items-center gap-1 rounded border border-bmade-200 px-2 py-1 text-bmade-700 hover:border-accent-400 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:text-accent-300"
                  title="Open mail-draft"
                >
                  <Mail className="h-3 w-3" />
                  Mail
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
